import React, { useState, useEffect } from 'react';
import { X, ChevronRight } from 'lucide-react';

const FestiveBanner = ({ text, backgroundColor, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentEmoji, setCurrentEmoji] = useState(0);
  const [sparkles, setSparkles] = useState([]);
  
  const emojis = ['🎆', '🎊', '🥳', '✨', '🎉', '🌟', '🎇', '💫'];
  
  useEffect(() => {
    const emojiTimer = setInterval(() => {
      setCurrentEmoji(prev => (prev + 1) % emojis.length);
    }, 1000);
    return () => clearInterval(emojiTimer);
  }, [emojis.length]);

  // Generate random sparkles
  useEffect(() => {
    const generateSparkles = () => {
      const newSparkles = [];
      for (let i = 0; i < 8; i++) {
        newSparkles.push({
          id: i,
          left: Math.random() * 100,
          top: Math.random() * 100,
          delay: Math.random() * 2,
          size: 4 + Math.random() * 6
        });
      }
      setSparkles(newSparkles);
    };
    generateSparkles();
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Corner Ribbon Banner - Top Left */}
      <div className="corner-ribbon-container">
        <div className="corner-ribbon" style={{ background: backgroundColor }}>
          <div className="ribbon-sparkles">
            {sparkles.map(sparkle => (
              <div
                key={sparkle.id}
                className="sparkle"
                style={{
                  left: `${sparkle.left}%`,
                  top: `${sparkle.top}%`,
                  animationDelay: `${sparkle.delay}s`,
                  width: `${sparkle.size}px`,
                  height: `${sparkle.size}px`
                }}
              />
            ))}
          </div>
          <span className="ribbon-emoji">{emojis[currentEmoji]}</span>
          <span className="ribbon-year">NEW YEAR</span>
        </div>
      </div>

      {/* Floating Side Banner - Left Side */}
      <div className={`side-banner-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="side-banner" style={{ background: backgroundColor }}>
          {/* Glow Effect */}
          <div className="banner-glow-effect"></div>
          
          {/* Animated Background Particles */}
          <div className="banner-particles">
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`particle particle-${i}`} />
            ))}
          </div>

          {/* Content */}
          <div className="banner-content">
            {/* Top Section with Year */}
            <div className="banner-header">
              <div className="year-display">
                <span className="year-prefix">Welcome</span>
                <span className="year-number">2026</span>
              </div>
              <div className="emoji-circle">
                <span className="rotating-emoji">{emojis[currentEmoji]}</span>
              </div>
            </div>

            {/* Message */}
            <div className="banner-message">
              <p className="message-line-1">New Dreams</p>
              <p className="message-line-2">New Adventures!</p>
            </div>

            {/* Countdown Mini */}
            <div className="mini-countdown">
              <span className="countdown-label">Countdown Started</span>
              <div className="countdown-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="decorative-stars">
              <span>✦</span>
              <span>✦</span>
              <span>✦</span>
            </div>
          </div>

          {/* Close Button */}
          <button className="banner-close-btn" onClick={handleClose}>
            <X size={14} />
          </button>

          {/* Collapse/Expand Toggle */}
          <button 
            className="banner-toggle-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ChevronRight size={16} className={isExpanded ? 'rotated' : ''} />
          </button>
        </div>
      </div>

      <style>{`
        /* Corner Ribbon */
        .corner-ribbon-container {
          position: fixed;
          top: 0;
          left: 0;
          z-index: 9997;
          overflow: hidden;
          width: 150px;
          height: 150px;
          pointer-events: none;
        }
        
        .corner-ribbon {
          position: absolute;
          top: 30px;
          left: -40px;
          width: 170px;
          padding: 8px 0;
          transform: rotate(-45deg);
          text-align: center;
          box-shadow: 0 5px 20px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          overflow: hidden;
        }
        
        .ribbon-sparkles {
          position: absolute;
          inset: 0;
        }
        
        .sparkle {
          position: absolute;
          background: white;
          border-radius: 50%;
          animation: sparkleAnim 2s infinite;
        }
        
        @keyframes sparkleAnim {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        
        .ribbon-emoji {
          font-size: 1rem;
          animation: bounce 1s infinite;
          position: relative;
          z-index: 1;
        }
        
        .ribbon-year {
          font-size: 0.7rem;
          font-weight: 900;
          color: white;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
          position: relative;
          z-index: 1;
          letter-spacing: 1px;
        }

        /* Side Banner */
        .side-banner-container {
          position: fixed;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          z-index: 9996;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .side-banner-container.collapsed {
          transform: translateY(-50%) translateX(-90%);
        }
        
        .side-banner-container.collapsed:hover {
          transform: translateY(-50%) translateX(-85%);
        }

        .side-banner {
          position: relative;
          width: 140px;
          padding: 20px 15px;
          border-radius: 0 20px 20px 0;
          box-shadow: 
            5px 0 30px rgba(0,0,0,0.3),
            inset 0 0 30px rgba(255,255,255,0.1);
          overflow: hidden;
        }
        
        .banner-glow-effect {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2) 0%, transparent 50%);
          animation: glowRotate 8s linear infinite;
        }
        
        @keyframes glowRotate {
          100% { transform: rotate(360deg); }
        }
        
        .banner-particles {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }
        
        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(255,255,255,0.6);
          border-radius: 50%;
          animation: floatUp 4s infinite;
        }
        
        .particle-0 { left: 10%; animation-delay: 0s; }
        .particle-1 { left: 25%; animation-delay: 0.5s; }
        .particle-2 { left: 40%; animation-delay: 1s; }
        .particle-3 { left: 55%; animation-delay: 1.5s; }
        .particle-4 { left: 70%; animation-delay: 2s; }
        .particle-5 { left: 85%; animation-delay: 2.5s; }
        .particle-6 { left: 15%; animation-delay: 3s; }
        .particle-7 { left: 45%; animation-delay: 3.5s; }
        .particle-8 { left: 75%; animation-delay: 0.3s; }
        .particle-9 { left: 30%; animation-delay: 1.8s; }
        .particle-10 { left: 60%; animation-delay: 2.3s; }
        .particle-11 { left: 90%; animation-delay: 1.2s; }
        
        @keyframes floatUp {
          0% { bottom: -10px; opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { bottom: 110%; opacity: 0; }
        }
        
        .banner-content {
          position: relative;
          z-index: 2;
          text-align: center;
        }
        
        .banner-header {
          margin-bottom: 15px;
        }
        
        .year-display {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .year-prefix {
          font-size: 0.7rem;
          color: rgba(255,255,255,0.8);
          text-transform: uppercase;
          letter-spacing: 3px;
          margin-bottom: 2px;
        }
        
        .year-number {
          font-size: 2.2rem;
          font-weight: 900;
          background: linear-gradient(180deg, #FFFFFF 0%, #FFD700 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-shadow: none;
          line-height: 1;
          animation: yearPulse 2s infinite;
        }
        
        @keyframes yearPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .emoji-circle {
          width: 45px;
          height: 45px;
          background: rgba(255,255,255,0.15);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 10px auto;
          border: 2px solid rgba(255,255,255,0.3);
          animation: emojiSpin 10s linear infinite;
        }
        
        @keyframes emojiSpin {
          100% { transform: rotate(360deg); }
        }
        
        .rotating-emoji {
          font-size: 1.5rem;
          animation: emojiCounter 10s linear infinite;
        }
        
        @keyframes emojiCounter {
          100% { transform: rotate(-360deg); }
        }
        
        .banner-message {
          margin: 15px 0;
        }
        
        .message-line-1 {
          font-size: 0.85rem;
          color: white;
          font-weight: 600;
          margin: 0;
          opacity: 0.9;
        }
        
        .message-line-2 {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.8);
          font-weight: 500;
          margin: 4px 0 0 0;
        }
        
        .mini-countdown {
          background: rgba(0,0,0,0.2);
          border-radius: 10px;
          padding: 8px;
          margin-top: 12px;
        }
        
        .countdown-label {
          font-size: 0.6rem;
          color: rgba(255,255,255,0.7);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .countdown-dots {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-top: 6px;
        }
        
        .dot {
          width: 8px;
          height: 8px;
          background: #FFD700;
          border-radius: 50%;
          animation: dotPulse 1.5s infinite;
        }
        
        .dot:nth-child(2) { animation-delay: 0.3s; }
        .dot:nth-child(3) { animation-delay: 0.6s; }
        
        @keyframes dotPulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.3); opacity: 1; }
        }
        
        .decorative-stars {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-top: 12px;
          color: #FFD700;
          font-size: 0.7rem;
        }
        
        .decorative-stars span {
          animation: starTwinkle 1.5s infinite;
        }
        
        .decorative-stars span:nth-child(2) { animation-delay: 0.5s; }
        .decorative-stars span:nth-child(3) { animation-delay: 1s; }
        
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        .banner-close-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(0,0,0,0.3);
          border: none;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          z-index: 10;
        }
        
        .banner-close-btn:hover {
          background: rgba(255,0,0,0.5);
          transform: scale(1.1);
        }
        
        .banner-toggle-btn {
          position: absolute;
          right: -12px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 40px;
          border-radius: 0 8px 8px 0;
          background: rgba(0,0,0,0.4);
          border: none;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          z-index: 10;
        }
        
        .banner-toggle-btn:hover {
          background: rgba(0,0,0,0.6);
        }
        
        .banner-toggle-btn .rotated {
          transform: rotate(180deg);
        }

        @media (max-width: 768px) {
          .corner-ribbon-container {
            width: 120px;
            height: 120px;
          }
          .corner-ribbon {
            top: 22px;
            left: -45px;
            width: 150px;
            padding: 6px 0;
            font-size: 0.8rem;
          }
          .side-banner-container {
            top: auto;
            bottom: 80px;
            left: 0;
            transform: none;
          }
          .side-banner-container.collapsed {
            transform: translateX(-90%);
          }
          .side-banner {
            width: 120px;
            padding: 15px 12px;
          }
          .year-number {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
};

export default FestiveBanner;
